import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { ticketDispatchQuery, type TicketDispatchPerson } from '../api/easeedesk'
import { Spinner } from '../ui/Spinner'
import { EscalateButton, TicketEscalateModal, type EscalateTarget } from './TicketEscalateModal'
import './TicketDispatch.css'

/**
 * Fila "Fast Dispatch" - `easeedesk/tickets/fast_dispatch/index.php`.
 *
 * Intrebarea filei: PE CINE TRIMIT ACOLO? Se ia sediul la care s-a deschis
 * tichetul si se aseaza tehnicienii pe inele de distanta - primii cei de langa.
 *
 * DOUA LISTE, ca in portal: cei din tura (cum se deschide fila) si toti
 * tehnicienii furnizorului.
 *
 * "Dispatch" e butonul "Escalate" de pe om (`fast_dispatch.js`: escaladare
 * interna catre om), desenat cand setarea `escalate_button` e aprinsa si
 * tichetul e deschis; deschide `TicketEscalateModal`.
 */

export function TicketDispatch({ id }: { id: number }) {
  const [pick, setPick] = useState({ office: 0, everyone: false })
  const [target, setTarget] = useState<EscalateTarget | null>(null)
  const { data, error } = useQuery(ticketDispatchQuery(id, pick.office, pick.everyone))

  if (error) {
    return (
      <div className="alert alert-danger" role="alert">
        Something went wrong: {error.message}
      </div>
    )
  }
  if (!data) return <Spinner />

  return (
    <div className={`fd-wrap fastdispatch-content-${id}`}>
      <div className="fd-head">
        {data.offices.length > 0 && (
          <select
            className="form-control input-sm fd-office"
            value={data.officeId}
            onChange={(ev) => setPick({ ...pick, office: Number(ev.target.value) })}
          >
            {data.offices.map((one) => (
              <option key={one.id} value={one.id}>
                {one.name}
              </option>
            ))}
          </select>
        )}

        <div className="btn-group" role="group">
          <button
            type="button"
            className={`btn btn-sm btn-theme-inverse btn-transparent${data.everyone ? '' : ' active'}`}
            onClick={() => setPick({ ...pick, everyone: false })}
          >
            On duty
          </button>
          <button
            type="button"
            className={`btn btn-sm btn-theme-inverse btn-transparent${data.everyone ? ' active' : ''}`}
            onClick={() => setPick({ ...pick, everyone: true })}
          >
            Everyone
          </button>
        </div>
      </div>

      {data.latitude === null || data.longitude === null ? (
        /* Fara coordonate pe sediu nu se poate masura nimic, si o lista fara
           distante nu e o lista de repartizare - e o agenda. Portalul desena
           atunci un panou gol, care semana cu unul care nu s-a incarcat. */
        <div className="alert alert-warning" role="alert">
          <i className="fas fa-exclamation-circle" aria-hidden="true"></i> {data.officeName || 'This office'} has no
          coordinates, so nobody can be measured against it.
        </div>
      ) : (
        <Bands bands={data.bands} everyone={data.everyone} settings={data.settings} escalate={data.settings.escalate && data.open ? { ticket: id, onPick: setTarget } : null} />
      )}

      <TicketEscalateModal ticket={id} target={target} onClose={() => setTarget(null)} />
    </div>
  )
}

function Bands({
  bands,
  everyone,
  settings,
  escalate,
}: {
  bands: { name: string; people: TicketDispatchPerson[] }[]
  everyone: boolean
  settings: { maxDistance: number; checkIn: boolean; commuting: boolean }
  escalate: { ticket: number; onPick(t: EscalateTarget): void } | null
}) {
  const anybody = bands.some((band) => band.people.length > 0)

  if (!anybody) {
    return (
      <div className="alert alert-info" role="alert">
        <i className="fas fa-info-circle" aria-hidden="true"></i>{' '}
        {everyone
          ? 'No technician of this service provider has a location written down.'
          : !settings.checkIn && !settings.commuting
            ? 'Neither check-in nor commuting is turned on, so nobody counts as on duty.'
            : 'Nobody is on duty within reach right now.'}
      </div>
    )
  }

  return (
    <>
      {bands.map((band) => (
        <section className="fd-band" key={band.name}>
          <h5 className="bordered">
            {band.name} km <span className="fd-muted">({band.people.length})</span>
          </h5>

          {band.people.length === 0 ? (
            <p className="fd-muted">Nobody.</p>
          ) : (
            <table className="table table-hover fd-table">
              <tbody>
                {band.people.map((one) => (
                  <tr key={one.id}>
                    <td>
                      <strong>{one.name}</strong>
                      <div className="fd-muted">{one.department}</div>
                    </td>
                    <td className="fd-km">{one.distance} km</td>
                    <td>
                      {one.phone}
                      {one.state !== '' && <div className="fd-muted">{one.state}{one.office !== '' ? ` ${one.office}` : ''}</div>}
                    </td>
                    {/* Cifra dupa care se alege intre doi oameni la fel de
                        aproape: cate tichete are deja pe el. */}
                    <td className="fd-km" title="Open tickets / resolved in the last month">
                      {one.openTickets} / {one.resolvedTickets}
                    </td>
                    {escalate && (
                      <td>
                        <EscalateButton ticket={escalate.ticket} person={{ user: one.id, group: one.groupId, name: one.name }} onPick={escalate.onPick} />
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>
      ))}
    </>
  )
}
